import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, Tabs, Timeline, Space, Button, Modal, Form, Input, message } from 'antd';
import { laborFeeCostBearers, CurrencyList } from '@utils/constants';
import { numberProject, formatCurrency } from '@utils/utils';
import { fetchLaborFeeApproval, fetchApprovalProcessList, fetchLaborFeeExaminationResult } from '@services/Finance/FinanceLaborFee.service.js';
import dayjs from 'dayjs';
import './FinanceApprovalDetailModal.less';
import PaymentApplicationDetailDrawer from './PaymentApplicationDetailDrawer';

const { TextArea } = Input;

const getTimelineStatusClassName = (status, nodeId) => {
    if ((nodeId === 10 && status === 3) || nodeId === -1) {
        return 'timeline-status--success';
    }
    const statusMap = {
        0: 'timeline-status--default',
        1: 'timeline-status--warning',
        2: 'timeline-status--success',
        3: 'timeline-status--error',
    };
    return statusMap[status] || 'timeline-status--default';
};

const FinanceApprovalDetailModal = props => {
    const {
        open,
        onClose,
        paymentRecord,
        projectId,
        batchNo, // 新增：批次号
        statisticalAmount,
        onRefresh, // 新增:审批成功后的刷新回调
        currentUserPhone, // 新增:当前登录用户手机号
        type,
    } = props;
    const navigate = useNavigate();
    const location = useLocation();

    // 审批意见弹窗相关状态
    const [approvalOpinionVisible, setApprovalOpinionVisible] = useState(false);
    const [approvalType, setApprovalType] = useState(null); // 'agree' or 'disagree'
    const [approvalForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // 付款申请明细弹窗状态
    const [paymentDetailVisible, setPaymentDetailVisible] = useState(false);

    // 弹窗内部状态
    const [approvalProcessList, setApprovalProcessList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [costBearersData, setCostBearersData] = useState([]); // 费用承担主体数据
    const [examinationData, setExaminationData] = useState(null); // 审批详情数据

    const defaultCurrencyText = CurrencyList.find(c => c.currency === 'CNY')?.name || '人民币';

    // 获取审批流程列表
    const getApprovalProcessList = async () => {
        if (!projectId || !batchNo) return;

        try {
            const params = {
                conferenceId: projectId,
                batchNo: batchNo,
            };
            const res = await fetchApprovalProcessList(params);
            if (res && res.status_code === 200) {
                // 倒序展示审批流程
                const reversedList = Array.isArray(res.data) ? [...res.data].reverse() : [];
                setApprovalProcessList(reversedList);
            }
        } catch (error) {
            console.error('获取审批流程列表失败:', error);
        }
    };

    // 获取审批详情数据（包含批次信息、付款信息、费用承担主体数据）
    const getCostBearersData = async () => {
        if (!projectId || !batchNo) return;

        try {
            const params = {
                projectId: projectId,
                batchNo: batchNo,
            };
            const res = await fetchLaborFeeExaminationResult(params);
            if (res && res.status_code === 200 && res.data) {
                // 保存完整的审批详情数据
                setExaminationData(res.data);

                // 提取并转换费用承担主体数据
                const costBearers = res.data.costBearers || [];
                const formattedData = costBearers.map(item => {
                    const costBearerValue = item.costBearer !== undefined && item.costBearer !== null ? item.costBearer : null;

                    // 查找外币字段
                    let foreignCurrencyField = null;
                    let foreignCurrencyValue = null;
                    let currency = 'CNY';

                    for (const field in item) {
                        if (field !== 'laborFeeAmount' && field !== 'individualTaxAmount' && field !== 'costBearer' && field !== 'expertSize' && field !== 'currency' && CurrencyList.some(c => c.currency === field)) {
                            foreignCurrencyField = field;
                            foreignCurrencyValue = item[field];
                            currency = field;
                            break;
                        }
                    }

                    const isForeignCurrency = currency !== 'CNY' && CurrencyList.some(c => c.currency === currency);
                    const baseCostBearerName = costBearerValue !== null ? laborFeeCostBearers.find(b => b.value === costBearerValue)?.label || '—' : '—';
                    const costBearerName = isForeignCurrency ? `${baseCostBearerName}（外币）` : baseCostBearerName;

                    return {
                        ...item,
                        costBearer: costBearerValue,
                        costBearerName: costBearerName,
                        currency: currency, // 保存货币信息
                        isForeignCurrency: isForeignCurrency, // 标记是否为外币
                        foreignCurrencyField: foreignCurrencyField, // 记录外币字段名
                        foreignCurrencyValue: parseFloat(foreignCurrencyValue) || 0,
                    };
                });
                setCostBearersData(formattedData);
            }
        } catch (error) {
            console.error('获取审批详情数据失败:', error);
        }
    };

    // 弹窗打开时加载数据
    useEffect(() => {
        if (open && projectId && batchNo) {
            setLoading(true);
            Promise.all([getApprovalProcessList(), getCostBearersData()]).finally(() => {
                setLoading(false);
            });
        }
    }, [open, projectId, batchNo]);

    // // 下载付款申请明细
    // const handlePrintPaymentDetail = () => {
    //     setPaymentDetailVisible(true);
    // };

    // 根据币种代码获取货币单位信息
    const getCurrencyInfo = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency
            ? {
                  majorUnit: currency.majorUnit,
                  minorUnits: currency.minorUnits,
              }
            : {
                  majorUnit: '元',
                  minorUnits: ['角', '分'],
              };
    };

    // 数字金额转大写（带货币单位）
    const convertNumberToChineseCurrencyWithUnit = (num, unit = '元', minorUnits = ['角', '分']) => {
        const chnNumChar = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];

        const numStr = (parseFloat(num) || 0).toFixed(2);
        const [intPart, decimalPart] = numStr.split('.');
        const intNum = parseInt(intPart, 10);

        // 处理四位以内的数字转换
        const convertSectionToWords = num => {
            if (num === 0) return '';

            let result = '';
            const qian = Math.floor(num / 1000);
            const bai = Math.floor((num % 1000) / 100);
            const shi = Math.floor((num % 100) / 10);
            const ge = num % 10;

            if (qian > 0) {
                result += chnNumChar[qian] + '仟';
            }
            if (bai > 0) {
                result += chnNumChar[bai] + '佰';
            } else if (qian > 0 && (shi > 0 || ge > 0)) {
                result += '零';
            }
            if (shi > 0) {
                result += chnNumChar[shi] + '拾';
            } else if ((qian > 0 || bai > 0) && ge > 0) {
                if (shi === 0 && bai === 0) {
                    result += '零';
                }
            }
            if (ge > 0) {
                result += chnNumChar[ge];
            }

            return result;
        };

        // 处理万位以上的数字
        if (intNum === 0) {
            return `零${unit}整`;
        }

        let result = '';

        // 处理万位及以上
        if (intNum >= 10000) {
            const wan = Math.floor(intNum / 10000);
            const remainder = intNum % 10000;

            result += convertSectionToWords(wan) + '万';

            if (remainder > 0) {
                if (remainder < 1000) {
                    result += '零';
                }
                result += convertSectionToWords(remainder);
            }
        } else {
            result = convertSectionToWords(intNum);
        }

        // 清理多余的零
        result = result.replace(/零+/g, '零').replace(/零$/g, '').replace(/^零/, '');

        result += unit;

        // 处理小数部分
        if (decimalPart) {
            const minorUnit1 = minorUnits[0] || '角'; // 默认为角
            const minorUnit2 = minorUnits[1] || '分'; // 默认为分

            const digit1 = parseInt(decimalPart[0], 10);
            const digit2 = parseInt(decimalPart[1], 10);

            if (digit1 === 0 && digit2 === 0) {
                result += '整';
            } else {
                if (digit1 > 0) {
                    result += chnNumChar[digit1] + minorUnit1;
                } else {
                    if (digit2 > 0) {
                        result += '零';
                    }
                }
                if (digit2 > 0) {
                    result += chnNumChar[digit2] + minorUnit2;
                }
            }
        } else {
            result += '整';
        }

        return result;
    };

    // 根据币种代码转换数字金额为大写
    const convertNumberToChineseCurrencyByCurrency = (num, currencyCode) => {
        const { majorUnit, minorUnits } = getCurrencyInfo(currencyCode);
        return convertNumberToChineseCurrencyWithUnit(num, majorUnit, minorUnits);
    };

    // 获取需要支付劳务费的人数
    const getTotalNeedLaborFee = () => {
        // 优先使用接口返回的数据
        const data = examinationData || statisticalAmount;
        if (data?.realTotal && (data?.LaborFeeNullSum || data?.LaborFeeNullSum === 0) && (data?.LaborFeeZeroSum || data?.LaborFeeZeroSum === 0)) {
            return data.realTotal - data.LaborFeeNullSum - data.LaborFeeZeroSum;
        }
        return 0;
    };

    // 获取含税金额
    const getTotalAmount = (LaborFeeSum, IndividualTaxSum) => {
        if (LaborFeeSum !== null && IndividualTaxSum !== null) {
            return (parseFloat(LaborFeeSum) + parseFloat(IndividualTaxSum)).toFixed(2);
        }
        return 0;
    };

    // 计算按钮显示逻辑
    const getButtonDisplayConfig = () => {
        const config = {
            showPrintBtn: false,
            showAgreeBtn: false,
            showDisagreeBtn: false,
            btnNodeId: null,
        };

        // 如果没有登录用户手机号或批次信息为空，不显示任何按钮
        if (!currentUserPhone || !paymentRecord) {
            return config;
        }

        // 使用批次信息中的节点信息
        const { nodeId, approvalBy, approvalStatus } = paymentRecord;

        // 获取审批流程列表中节点5和节点6的审批人
        const node5Approver = approvalProcessList.find(node => node.nodeId === 5)?.approvalBy;
        const node6Approver = approvalProcessList.find(node => node.nodeId === 6)?.approvalBy;

        // 当前登录人是否为当前节点审批人
        const isCurrentNodeApprover = approvalBy === currentUserPhone;

        // 节点5：财务经理审核
        if (nodeId === 5) {
            if (isCurrentNodeApprover) {
                // 待审核（approvalStatus=1）：展示同意和不同意按钮
                // 已审核（approvalStatus=2）：只展示不同意按钮
                if (approvalStatus === 1) {
                    config.showAgreeBtn = true;
                }
                config.showDisagreeBtn = true;
                config.btnNodeId = nodeId;
            }
            // 非当前节点审批人：不展示按钮
        }
        // 节点6：财务总监审批
        else if (nodeId === 6) {
            if (isCurrentNodeApprover) {
                // 待审核（approvalStatus=1）：展示同意和不同意按钮
                // 已审核（approvalStatus=2）：只展示不同意按钮
                if (approvalStatus === 1) {
                    config.showAgreeBtn = true;
                }
                config.showDisagreeBtn = true;
                config.btnNodeId = nodeId;
            } else if (node5Approver === currentUserPhone) {
                // 非当前节点审批人，但是节点5的审批人：展示不同意按钮
                config.showDisagreeBtn = true;
                config.btnNodeId = 5;
            }
            // 既非当前节点审批人也非节点5审批人：不展示按钮
        }
        // 节点10：付款节点（approvalStatus=2 为未付款，approvalStatus=3 为全部付款）
        else if (nodeId === 10) {
            // 全部付款（approvalStatus=3）：不展示任何按钮
            if (approvalStatus !== 3) {
                if (isCurrentNodeApprover) {
                    // 未付款（approvalStatus=2）：展示不同意按钮
                    config.showDisagreeBtn = true;
                    config.btnNodeId = nodeId;
                } else if (node5Approver === currentUserPhone || node6Approver === currentUserPhone) {
                    // 非当前节点审批人，但是节点5或节点6的审批人：展示不同意按钮
                    config.showDisagreeBtn = true;
                    config.btnNodeId = node6Approver === currentUserPhone ? 6 : 5;
                }
                // 既非当前节点审批人也非节点5/6审批人：不展示按钮
            }
        }

        // console.log('Button Display Config:', config);
        return config;
    };

    const buttonConfig = getButtonDisplayConfig();

    // 处理同意按钮点击
    const handleAgreeClick = () => {
        setApprovalType('agree');
        setApprovalOpinionVisible(true);
    };

    // 处理不同意按钮点击
    const handleDisagreeClick = () => {
        setApprovalType('disagree');
        setApprovalOpinionVisible(true);
    };

    // 处理审批意见弹窗确认
    const handleApprovalOpinionOk = async () => {
        try {
            const values = await approvalForm.validateFields();

            // 获取当前登录人有权限审批的节点ID
            // 若审批为同意，从审批流程列表中找到审批人与当前登录人一致的且审批状态1的节点ID
            // 若审批为不同意，从审批流程列表中找到审批人与当前登录人一致的且审批状态2的第一个节点的ID
            // let matchingNode;
            // if (approvalType === 'agree') {
            //     matchingNode = approvalProcessList.find(node => node.approvalBy === currentUserPhone && node.approvalStatus === 1);
            // } else {
            //     matchingNode = approvalProcessList.find(node => node.approvalBy === currentUserPhone && node.approvalStatus === 2);
            // }
            // const nodeId = matchingNode ? matchingNode.nodeId : paymentRecord?.nodeId;

            const nodeId = getButtonDisplayConfig().btnNodeId;

            if (!nodeId) {
                message.error('未找到有效的审批节点');
                return;
            }

            // 根据审批类型显示不同的确认提示
            let confirmMessage = '';
            if (approvalType === 'agree') {
                confirmMessage = '请确认审批无误，确认后不允许修改';
            } else {
                confirmMessage = '确认后将退回至经办人';
            }

            Modal.confirm({
                title: '确认提示',
                content: confirmMessage,
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: async () => {
                    setSubmitting(true);
                    try {
                        const res = await fetchLaborFeeApproval({
                            nodeId,
                            projectId,
                            status: approvalType === 'agree' ? 1 : 2,
                            rejectedReason: values.rejectedReason || '',
                            batchNo,
                        });

                        if (res && res.status_code === 200) {
                            const successMsg = approvalType === 'agree' ? '审批结束，已提交至下一节点' : '审批结束，已退回至经办人';
                            message.success(successMsg);

                            // 关闭所有弹窗
                            setApprovalOpinionVisible(false);
                            approvalForm.resetFields();
                            onClose();

                            // 刷新审批流程数据
                            await getApprovalProcessList();

                            // 调用刷新回调
                            if (onRefresh) {
                                onRefresh();
                            }
                        }
                    } catch (error) {
                        console.error('审批操作失败:', error);
                        message.error('审批操作失败，请重试');
                    } finally {
                        setSubmitting(false);
                    }
                },
            });
        } catch (error) {
            // 表单验证失败
            console.log('表单验证失败:', error);
        }
    };

    // 处理审批意见弹窗取消
    const handleApprovalOpinionCancel = () => {
        setApprovalOpinionVisible(false);
        approvalForm.resetFields();
        setApprovalType(null);
    };

    // 处理关闭弹窗
    const handleClose = () => {
        // 检查URL参数中是否有fromPage，如果有则返回到该页面
        const urlParams = new URLSearchParams(location.search || window.location.search);
        const fromPage = urlParams.get('fromPage');

        if (fromPage && fromPage !== window.location.pathname) {
            navigate(fromPage, { replace: true });
        } else {
            navigate({ pathname: location.pathname, search: '' }, { replace: true });
        }

        onClose();
    };

    // 审批流程 Tab
    const ApprovalFlowTab = () => {
        const dataToDisplay = approvalProcessList || [];

        return (
            <div className="approval-flow-tab">
                {dataToDisplay.length > 0 ? (
                    <Timeline
                        className="payment-flow-container"
                        items={dataToDisplay.map((node, index) => {
                            const statusClassName = getTimelineStatusClassName(node.approvalStatus, node.nodeId);
                            const isSubmitted = node.nodeId === -1 || node.approvalStatus === 2 || (node.nodeId === 10 && node.approvalStatus === 3);
                            const icon = isSubmitted ? <i className="bi bi-check-circle"></i> : <i className="bi bi-clock"></i>;
                            const hasOpinion = node.rejectedReason && node.rejectedReason.trim() !== '';
                            const headerSpaced = node.approvalByName || hasOpinion;

                            return {
                                key: `node_${node.nodeId}_${index}`,
                                dot: icon,
                                children: (
                                    <div className="timeline-content">
                                        <div className={`timeline-content__header${headerSpaced ? ' timeline-content__header--spaced' : ''}`}>
                                            <span className="timeline-content__node-name">{node.nodeName}</span>
                                            <span className={`timeline-content__status ${statusClassName}`}>{node.approvalStatusName}</span>
                                        </div>
                                        {node.approvalByName && (
                                            <div className={`timeline-content__meta${hasOpinion ? ' timeline-content__header--spaced' : ''}`}>
                                                <span className="timeline-content__approver">{node.approvalByName}</span>
                                                <span className="timeline-content__time">{node.approvalAt || ''}</span>
                                            </div>
                                        )}
                                        {hasOpinion && (
                                            <div className="timeline-content__opinion">
                                                <strong>审批意见：</strong>
                                                <span>{node.rejectedReason}</span>
                                            </div>
                                        )}
                                    </div>
                                ),
                            };
                        })}
                    />
                ) : (
                    <div className="approval-flow-empty">暂无审批流程数据</div>
                )}
            </div>
        );
    };

    return (
        <Drawer
            closable={{ placement: 'end' }}
            title={<div style={{ textAlign: 'center', width: '100%' }}>审批详情</div>}
            placement="bottom"
            onClose={handleClose}
            open={open}
            size="100vh"
            styles={{
                body: { padding: 0, overflow: 'hidden' },
                header: { borderBottom: '1px solid #f0f0f0' },
                wrapper: { height: '100vh' },
                footer: { borderTop: '1px solid #f0f0f0', padding: '16px 24px' },
            }}
            footer={
                buttonConfig.showAgreeBtn || buttonConfig.showDisagreeBtn ? (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0' }}>
                        <Space>
                            {/* {buttonConfig.showPrintBtn && (
                                <Button icon={<i className="bi bi-printer"></i>} onClick={handlePrintPaymentDetail}>
                                    下载付款申请明细
                                </Button>
                            )} */}
                            {buttonConfig.showDisagreeBtn && (
                                <Button danger onClick={handleDisagreeClick}>
                                    不同意
                                </Button>
                            )}
                            {buttonConfig.showAgreeBtn && (
                                <Button type="primary" onClick={handleAgreeClick}>
                                    同意
                                </Button>
                            )}
                        </Space>
                    </div>
                ) : null
            }
            className="finance-approval-detail-modal"
        >
            <div style={{ display: 'flex', height: '100%' }}>
                {/* 左侧：审批详情内容 */}
                <div style={{ flex: 1, overflowY: 'auto', borderRight: '1px solid #f0f0f0', padding: '24px' }}>
                    <div className="approval-detail-wrap" id="approval_detail_wrap">
                        <div className="batch-info">
                            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>批次信息</div>
                            <div className="module-content">
                                <div>
                                    <div>
                                        付款申请批次信息：<span>{paymentRecord?.batchCreatedAt ? dayjs(paymentRecord.batchCreatedAt).format('YYYY年M月D日 HH:mm:ss') : '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        经办人：<span>{examinationData?.handlerName || paymentRecord?.approvalByName || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="labor-project-info">
                            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>项目信息</div>
                            <div className="module-content">
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <div>
                                        项目名称：<span>{examinationData?.name || '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        氚云编号：<span>{examinationData?.h3yunNo || '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        项目编号：<span>{numberProject('YHY', projectId) || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="approval-info">
                            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>付款信息</div>
                            <div className="module-content">
                                <div>
                                    <div>
                                        劳务申请人数：<span>{examinationData?.realTotal || statisticalAmount?.realTotal || 0}人</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        需要劳务费人数：<span>{getTotalNeedLaborFee()}人</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        实发劳务费合计金额（元）：<span>{formatCurrency(examinationData?.LaborFeeSum || statisticalAmount?.LaborFeeSum)}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：
                                        <span>
                                            {defaultCurrencyText}
                                            {convertNumberToChineseCurrencyWithUnit(examinationData?.LaborFeeSum || statisticalAmount?.LaborFeeSum || 0)}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        个人所得税合计金额（元）：<span>{formatCurrency(examinationData?.IndividualTaxSum || statisticalAmount?.IndividualTaxSum)}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：
                                        <span>
                                            {defaultCurrencyText}
                                            {convertNumberToChineseCurrencyWithUnit(examinationData?.IndividualTaxSum || statisticalAmount?.IndividualTaxSum || 0)}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        含税劳务费合计金额（元）：<span>{formatCurrency(getTotalAmount(examinationData?.LaborFeeSum || statisticalAmount?.LaborFeeSum, examinationData?.IndividualTaxSum || statisticalAmount?.IndividualTaxSum))}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        金额（大写）：
                                        <span>
                                            {defaultCurrencyText}
                                            {convertNumberToChineseCurrencyWithUnit(getTotalAmount(examinationData?.LaborFeeSum || statisticalAmount?.LaborFeeSum, examinationData?.IndividualTaxSum || statisticalAmount?.IndividualTaxSum))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {costBearersData.length > 0
                            && costBearersData.map(costBearerItem => {
                                // 如果是外币，使用外币相关的数据
                                const laborFeeAmount = parseFloat(costBearerItem.laborFeeAmount) || 0;
                                const individualTaxAmount = parseFloat(costBearerItem.individualTaxAmount) || 0;
                                const totalAmount = (laborFeeAmount + individualTaxAmount).toFixed(2);

                                // 确定货币单位
                                const currencyUnit = costBearerItem.isForeignCurrency ? CurrencyList.find(c => c.currency === costBearerItem.currency)?.name || costBearerItem.currency : '元';

                                return (
                                    <div
                                        key={`cost_bearer_${costBearerItem.costBearer}_${costBearerItem.isForeignCurrency ? `${laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}（外币）` : laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}`}
                                        className="cost-bearers"
                                    >
                                        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                                            支付主体：{costBearerItem.isForeignCurrency ? `${laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}（外币）` : laborFeeCostBearers.find(b => b.value === costBearerItem.costBearer)?.label || '—'}
                                        </div>
                                        <div className="module-content">
                                            <div>
                                                需要劳务费人数：<span>{costBearerItem.expertSize}人</span>
                                            </div>
                                            <div></div>
                                            {costBearerItem.isForeignCurrency ? (
                                                // 外币支付时的展示项
                                                <>
                                                    <div>
                                                        外币实发额合计（{currencyUnit}）：<span>{formatCurrency(costBearerItem.foreignCurrencyValue)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {currencyUnit}
                                                            {convertNumberToChineseCurrencyByCurrency(costBearerItem.foreignCurrencyValue, costBearerItem.currency)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        折合人民币实发劳务费合计金额（元）：<span>{formatCurrency(laborFeeAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(laborFeeAmount)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        折合人民币个人所得税合计金额（元）：<span>{formatCurrency(individualTaxAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(individualTaxAmount)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        折合人民币含税劳务费合计金额（元）：<span>{formatCurrency(totalAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(totalAmount)}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                // 非外币支付时的展示项
                                                <>
                                                    <div>
                                                        实发劳务费合计金额（元）：<span>{formatCurrency(laborFeeAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(laborFeeAmount)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        个人所得税合计金额（元）：<span>{formatCurrency(individualTaxAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(individualTaxAmount)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        含税劳务费合计金额（元）：<span>{formatCurrency(totalAmount)}</span>
                                                    </div>
                                                    <div>
                                                        金额（大写）：
                                                        <span>
                                                            {defaultCurrencyText}
                                                            {convertNumberToChineseCurrencyWithUnit(totalAmount)}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* 右侧：审批流程Tab */}
                <div className="custom-tab-container">
                    <Tabs
                        defaultActiveKey="approval"
                        items={[
                            {
                                key: 'approval',
                                label: '审批流程',
                                children: <ApprovalFlowTab />,
                            },
                        ]}
                        style={{ flex: 1 }}
                        tabBarStyle={{ paddingLeft: '16px', borderBottom: '1px solid #f0f0f0' }}
                    />
                </div>
            </div>

            {/* 审批意见弹窗 */}
            <Modal title={approvalType === 'agree' ? '审批同意意见' : '审批不同意意见'} open={approvalOpinionVisible} onOk={handleApprovalOpinionOk} onCancel={handleApprovalOpinionCancel} okText="确认" cancelText="取消" centered confirmLoading={submitting}>
                <Form form={approvalForm} layout="vertical">
                    <Form.Item
                        name="rejectedReason"
                        label={approvalType === 'agree' ? '同意理由' : '不同意理由（确认后该劳务费申请，将退回至经办人）'}
                        rules={[
                            {
                                required: approvalType === 'disagree',
                                message: '请输入不同意理由',
                            },
                        ]}
                    >
                        <TextArea
                            showCount
                            maxLength={100}
                            placeholder={approvalType === 'agree' ? '请输入同意理由' : '请输入不同意理由'}
                            style={{
                                height: 120,
                                resize: 'none',
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 付款申请明细弹窗 */}
            {paymentDetailVisible && <PaymentApplicationDetailDrawer open={paymentDetailVisible} onClose={() => setPaymentDetailVisible(false)} batchNo={batchNo} projectId={projectId} />}
        </Drawer>
    );
};

export default FinanceApprovalDetailModal;
